import { JSX } from 'react'

import {
  AutoSizer,
  InfiniteLoader,
  List,
  ListRowRenderer,
  WindowScroller,
} from 'react-virtualized'

import { LoadingSpinner } from '~/components/component-library/LoadingSpinner'

// https://github.com/bvaughn/react-virtualized/blob/master/docs/creatingAnInfiniteLoadingList.md
export function InfiniteScroll<T>({
  hasNextPage,
  isNextPageLoading,
  list,
  loadNextPage,
  renderItem,
  itemHeight = 90,
}: {
  hasNextPage: boolean
  isNextPageLoading: boolean
  list: T[]
  loadNextPage: () => Promise<T[] | undefined>
  renderItem: (event: T) => JSX.Element
  itemHeight?: number
}) {
  const rowCount = hasNextPage ? list.length + 1 : list.length

  const loadMoreRows = async () => {
    if (isNextPageLoading) {
      return
    }
    await loadNextPage()
  }

  const isRowLoaded = ({ index }: { index: number }) =>
    !hasNextPage || index < list.length

  const rowRenderer: ListRowRenderer = ({ index, key, style }) => {
    // Use item ID as key if available, fallback to array index for loading states
    // reason is that react-virtualized uses indices as keys and that messes with AnimatedNumber
    const itemKey = isRowLoaded({ index })
      ? (list[index] as { id: string })?.id || `item-${index}`
      : key

    return (
      <div key={itemKey} style={style}>
        {isRowLoaded({ index }) ? (
          renderItem(list[index])
        ) : (
          <div className="flex h-16 items-center justify-center text-secondary">
            <LoadingSpinner size="xl" />
          </div>
        )}
      </div>
    )
  }

  return (
    <InfiniteLoader
      isRowLoaded={isRowLoaded}
      loadMoreRows={loadMoreRows}
      rowCount={rowCount}
      threshold={15}
    >
      {({ onRowsRendered, registerChild: registerChildInfiniteLoader }) => (
        <WindowScroller>
          {({
            height,
            isScrolling,
            onChildScroll,
            scrollTop,
            registerChild: registerChildWindowScroller,
          }) => (
            <AutoSizer disableHeight>
              {({ width }) => (
                <div ref={registerChildWindowScroller}>
                  <List
                    autoHeight
                    className="no-scrollbar !overflow-visible focus:outline-none"
                    ref={registerChildInfiniteLoader}
                    isScrolling={isScrolling}
                    onScroll={onChildScroll}
                    onRowsRendered={onRowsRendered}
                    rowRenderer={rowRenderer}
                    rowCount={rowCount}
                    rowHeight={itemHeight}
                    width={width}
                    height={height}
                    scrollTop={scrollTop}
                  />
                </div>
              )}
            </AutoSizer>
          )}
        </WindowScroller>
      )}
    </InfiniteLoader>
  )
}
